import { DateInputLocalized } from '@components'

import {
  Divider,
  Flex,
  FormItem as FormItemDS,
  FormItemsGroup,
  Icon,
  Input,
  Module,
  NavItem,
  Select,
  Space,
  Spacing,
  Text,
  Textarea,
} from '@billy/design-system'
import { addDays } from 'date-fns'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_INVOICE_PAYMENT_TERM } from '@features/invoices/constants/defaultInvoicePaymentTerm'
import { INVOICE_FOOTER_CHARACTER_LIMIT } from '@features/invoices/constants/invoiceFooterCharacterLimit'
import { INVOICE_MESSAGE_CHARACTER_LIMIT } from '@features/invoices/constants/invoiceMessageCharacterLimit'
import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { InvoiceFormReceiver } from '@features/invoices/types/invoiceForm/invoiceFormReceiver'
import { getInvoiceNumberDisplayedValue } from '@features/invoices/utils/getInvoiceNumberDisplayedValue'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useFormContext, useWatch } from '../../../../hooks'
import { DEMO_INVOICE_CUSTOMERS } from '../../constants/demoInvoiceCustomers'
import { DEMO_INVOICE_NUMBER, DEMO_INVOICE_NUMBER_PREFIX } from '../../constants/demoInvoiceNumber'
import { useTrackDemoInvoiceEditor } from '../../hooks/useTrackDemoInvoiceEditor'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'
import { getDemoInvoiceReceiver } from '../../utils/getDemoInvoiceReceiver'
import { DemoInvoiceLines } from '../DemoInvoiceLines'
import { DemoInvoicePayments } from '../DemoInvoicePayments'
import * as Styled from './styles'

interface DemoInvoiceFormProps {
  discountMode: InvoiceDiscountMode
  onDiscountModeChange: (discountMode: InvoiceDiscountMode) => void
}

export const DemoInvoiceForm = ({ discountMode, onDiscountModeChange }: DemoInvoiceFormProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { trackDemoInvoiceEditor } = useTrackDemoInvoiceEditor()
  const { control, FormItem, getValues, setValue } = useFormContext<DemoInvoiceFormSchema>()
  const [footerNote, message, paymentTerm] = useWatch({ control, name: ['footerNote', 'message', 'paymentTerm'] })

  const customerItems: NavItem<InvoiceFormReceiver>[] = useMemo(
    () =>
      DEMO_INVOICE_CUSTOMERS.map((customer) => {
        const receiver = getDemoInvoiceReceiver(customer, t)

        return { children: receiver.name, id: receiver.id, value: receiver }
      }),
    [t],
  )

  const handleReceiverDropdownOpen = useCallback(() => {
    trackDemoInvoiceEditor('Form - Customer - dropdown opened')
  }, [trackDemoInvoiceEditor])

  const handleReceiverSelect = useCallback(
    (onChange: (receiver?: InvoiceFormReceiver) => void, receiver?: InvoiceFormReceiver) => {
      const receiverPaymentTerm = receiver?.paymentTermsDays ?? DEFAULT_INVOICE_PAYMENT_TERM

      onChange(receiver)
      setValue('paymentTerm', receiverPaymentTerm)
      setValue('dueDate', addDays(getValues('issueDate'), receiverPaymentTerm))
    },
    [getValues, setValue],
  )

  const handleIssueDateChange = useCallback(
    (onChange: (issueDate?: Date | null) => void, issueDate?: Date | null) => {
      onChange(issueDate)

      if (issueDate) {
        setValue('dueDate', addDays(issueDate, paymentTerm))
      }
    },
    [paymentTerm, setValue],
  )

  return (
    <Module>
      <Module.SectionsGroup>
        <Module.Section>
          <Text nowrap truncate uppercase variant="h4" weight="bold">
            {getInvoiceNumberDisplayedValue(DEMO_INVOICE_NUMBER, DEMO_INVOICE_NUMBER_PREFIX)}
          </Text>
          <Space size="s16" />
          <Divider />
          <Space size="s16" />
          <FormItemsGroup columnSpacing={Spacing.S32} itemsInRow={2}>
            <FormItem
              name="receiver"
              label={t('external_invoices.editor.form.field.receiver.label')}
              render={({ field: { value, onChange, ...rest } }) => (
                <Select<InvoiceFormReceiver>
                  {...rest}
                  dropdownSize="fitTrigger"
                  items={customerItems}
                  onDropdownOpen={handleReceiverDropdownOpen}
                  onSelect={(_id, receiver) => handleReceiverSelect(onChange, receiver)}
                  selectedId={value?.id}
                  size="sm"
                  withSearch={false}
                />
              )}
            />
            <FormItemDS label={t('external_invoices.editor.form.field.issuer.label')}>
              <Input
                disabled
                prefix={<Icon icon="briefcaseBusiness" />}
                readOnly
                size="sm"
                value={organization?.name}
              />
            </FormItemDS>
          </FormItemsGroup>
          <Space />
          <FormItemsGroup columnSpacing={Spacing.S32} itemsInRow={4}>
            <FormItem
              itemColumnEnd={3}
              itemColumnStart={1}
              label={
                <Flex justifyContent="space-between">
                  <Text colorVariant="secondary">{t('external_invoices.editor.form.field.invoice_message.label')}</Text>
                  <Space horizontal size="s24" />
                  <Text colorVariant="secondary">{`${message.length}/${INVOICE_MESSAGE_CHARACTER_LIMIT}`}</Text>
                </Flex>
              }
              name="message"
              render={({ field }) => (
                <Textarea
                  {...field}
                  expandOnType
                  maxHeight={160}
                  maxLength={INVOICE_MESSAGE_CHARACTER_LIMIT}
                  resizable={false}
                  rows={1}
                  size="sm"
                />
              )}
            />
            <Styled.DateFieldsWrapper>
              <FormItemsGroup columnSpacing={Spacing.S16} itemsInRow={2}>
                <FormItem
                  name="issueDate"
                  label={t('external_invoices.editor.form.field.issue_date.label')}
                  render={({ field: { onChange, ...rest } }) => (
                    <DateInputLocalized
                      {...rest}
                      onChange={(issueDate) => handleIssueDateChange(onChange, issueDate)}
                      size="sm"
                    />
                  )}
                />
                <FormItem
                  name="dateOfSaleOrService"
                  label={t('external_invoices.editor.form.field.date_of_sale_or_service.label')}
                  render={({ field }) => <DateInputLocalized {...field} size="sm" />}
                />
              </FormItemsGroup>
            </Styled.DateFieldsWrapper>
          </FormItemsGroup>
        </Module.Section>
        <Module.Section title={t('external_invoices.editor.form.field.invoice_lines.label')}>
          <DemoInvoiceLines discountMode={discountMode} onDiscountModeChange={onDiscountModeChange} />
        </Module.Section>
        <Module.Section>
          <FormItemsGroup columnSpacing={Spacing.S32} itemsInRow={2}>
            <Styled.PaymentsWrapper>
              <DemoInvoicePayments />
            </Styled.PaymentsWrapper>
            <FormItem
              label={
                <Flex flexDirection="column">
                  <Text variant="h4">{t('external_invoices.editor.form.field.invoice_footer_note.header')}</Text>
                  <Space />
                  <Flex justifyContent="space-between">
                    <Text colorVariant="secondary">
                      {t('external_invoices.editor.form.field.invoice_footer_note.label')}
                    </Text>
                    <Text colorVariant="secondary">{`${footerNote.length}/${INVOICE_FOOTER_CHARACTER_LIMIT}`}</Text>
                  </Flex>
                </Flex>
              }
              name="footerNote"
              render={({ field }) => (
                <Textarea
                  {...field}
                  expandOnType
                  maxHeight={160}
                  maxLength={INVOICE_FOOTER_CHARACTER_LIMIT}
                  resizable={false}
                  rows={1}
                  size="sm"
                />
              )}
            />
          </FormItemsGroup>
        </Module.Section>
      </Module.SectionsGroup>
    </Module>
  )
}
